import { useState, useEffect, useCallback } from 'react';

export interface SavedScenario {
  id: string;
  name: string;
  createdAt: number;
  color: string;
  inputs: {
    callsPerMonth: number;
    numberOfProperties: number;
    missedCallPercent: number;
    salesCallPercent: number;
    avgOrderValue: number;
    staffHoursWeek: number;
    hourlyStaffCost: number;
  };
  results: {
    revenueLost: number;
    promoterImpact: number;
    costSavings: number;
    totalYearly: number;
    paybackMonths: number;
    yearOneROI: number;
  };
}

const STORAGE_KEY = 'hostcall-roi-scenarios';
const MAX_SCENARIOS = 5;
const SCENARIO_COLORS = ['#F05B27', '#22C55E', '#3B82F6', '#A855F7', '#F59E0B'];

export const useSavedScenarios = () => {
  const [scenarios, setScenarios] = useState<SavedScenario[]>([]);
  const [compareMode, setCompareMode] = useState(false);
  const [selectedForCompare, setSelectedForCompare] = useState<string[]>([]);

  // Load from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        setScenarios(JSON.parse(stored));
      } catch (e) {
        console.error('Failed to parse saved scenarios');
      }
    }
  }, []);

  const saveScenario = useCallback((
    name: string,
    inputs: SavedScenario['inputs'],
    results: SavedScenario['results']
  ) => {
    const newScenario: SavedScenario = {
      id: crypto.randomUUID(),
      name,
      createdAt: Date.now(),
      color: SCENARIO_COLORS[scenarios.length % SCENARIO_COLORS.length],
      inputs,
      results
    };
    
    setScenarios(prev => {
      const updated = [newScenario, ...prev].slice(0, MAX_SCENARIOS);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      return updated;
    });
    
    return newScenario.id;
  }, [scenarios.length]);

  const deleteScenario = useCallback((id: string) => {
    setScenarios(prev => {
      const updated = prev.filter(s => s.id !== id);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      return updated;
    });
    setSelectedForCompare(prev => prev.filter(sid => sid !== id));
  }, []);

  const toggleScenarioForCompare = useCallback((id: string) => {
    setSelectedForCompare(prev => {
      if (prev.includes(id)) {
        return prev.filter(sid => sid !== id);
      }
      if (prev.length >= 3) {
        return [...prev.slice(1), id]; // Max 3 for comparison
      }
      return [...prev, id];
    });
  }, []);

  const loadScenario = useCallback((id: string) => {
    return scenarios.find(s => s.id === id);
  }, [scenarios]);

  const clearAllScenarios = useCallback(() => {
    setScenarios([]);
    setSelectedForCompare([]);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  return {
    scenarios,
    compareMode,
    setCompareMode,
    selectedForCompare,
    saveScenario,
    deleteScenario,
    toggleScenarioForCompare,
    loadScenario,
    clearAllScenarios
  };
};

export default useSavedScenarios;
